import React, { useState, useRef, Children, lazy } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Sprout, Building2, Package, ArrowRight, Linkedin, ExternalLink, X } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { MyceliumText, PulsingNode, RevealWrapper, organicEase } from '../components/MyceliumAnimations';
const portfolioData = {
  grow: {
    title: 'GROW',
    subtitle: 'AGRICULTURE',
    allocation: '40%',
    color: '#2BC7B7',
    icon: Sprout,
    companies: [{
      id: 'bopa',
      name: 'BOPA',
      logo: 'https://cdn.magicpatterns.com/uploads/p75Pt4T4irpKShWjQq5kQt/BOPA_Logos_Primary_2color-SlatePink.png',
      headshot: 'https://cdn.magicpatterns.com/uploads/j5YeKjhxpiTiQqzWAJbVtt/Sarah_Headshot.jpg',
      founder: 'Sarah Hinkley',
      title: 'Founder & CEO',
      tagline: 'Autonomous nano-tractors for Regenerative Agriculture',
      stage: 'Seed',
      stageIcon: '🌿',
      opportunity: '$163B',
      traction: '$5M in sales pipeline',
      coInvestors: 'FirstMile Ventures',
      website: 'https://barnowlag.com/',
      linkedin: 'https://www.linkedin.com/company/bopa-precision-ag/',
      angle: 0,
      distance: 220
    }, {
      id: 'tierrasphere',
      name: 'TierraSphere',
      logo: 'https://cdn.magicpatterns.com/uploads/98gSPHpRjHPGHmXTjPPktJ/TierraSphere-Logo_(1).png',
      headshot: 'https://cdn.magicpatterns.com/uploads/9L48deLNJpBFgHmaVDUwzx/Marcela_Headshot.jpg',
      founder: 'Marcela Flores',
      title: 'Founder & CEO',
      tagline: 'AI For Soil Regeneration',
      stage: 'Pre-Seed',
      stageIcon: '🌱',
      opportunity: '$10B',
      traction: '30+ brands in pipeline, including PepsiCo',
      coInvestors: 'Carbon13 Backed',
      website: 'https://www.tierrasphere.com/',
      linkedin: 'https://www.linkedin.com/company/tierrasphere/',
      angle: -60,
      distance: 160
    }]
  },
  build: {
    title: 'BUILD',
    subtitle: 'BUILT ENVIRONMENT',
    allocation: '40%',
    color: '#4DE7D4',
    icon: Building2,
    companies: [{
      id: 'reefcycle',
      name: 'ReefCycle',
      logo: 'https://cdn.magicpatterns.com/uploads/38zm79CuEt3kRT2MvHZqxo/reefcycle-logo-black.png',
      headshots: ['https://cdn.magicpatterns.com/uploads/4hhMPidW6aSsMkoc11grzo/Mary_Headshot.jpg', 'https://cdn.magicpatterns.com/uploads/dDkjGRK46uLePih7U1qyMZ/Shannon_Headshot.jpg'],
      founders: ['Mary Lempres', 'Shannon Parker'],
      titles: ['Founder & CEO', 'Co-founder'],
      tagline: 'Nature-inspired cement',
      stage: 'Pre-Seed',
      stageIcon: '🌱',
      opportunity: '$36B',
      traction: 'NCSU testing complete, 2nd patent pending',
      coInvestors: 'Vitality is the first VC',
      website: 'https://reefcycle.earth/',
      linkedin: 'https://www.linkedin.com/company/reefcycle/posts/?feedView=all',
      angle: 90,
      distance: 170
    }]
  },
  use: {
    title: 'USE',
    subtitle: 'CONSUMER PRODUCTS',
    allocation: '20%',
    color: '#D4C5A9',
    icon: Package,
    companies: [{
      id: 'mothership',
      name: 'Mothership Materials',
      logo: 'https://cdn.magicpatterns.com/uploads/jjDdoGf7sEwKjjTyW7U3fB/MM_logo.webp',
      headshot: 'https://cdn.magicpatterns.com/uploads/gRrYe5nDZcZx5f2NZAUVvp/Jo_Headshot.jpg',
      founder: 'Jo Marini',
      title: 'Co-founder & CEO',
      tagline: 'Containerized biomanufacturing',
      stage: 'Seed',
      stageIcon: '🌿',
      opportunity: '$360B',
      traction: '3-year pilot with AB InBev',
      coInvestors: 'Refashiond Ventures',
      website: 'https://www.mothershipmaterials.com/',
      linkedin: 'https://www.linkedin.com/company/mothershipmaterials/',
      angle: 190,
      distance: 210
    }]
  }
};
export function PortfolioRadialGarden() {
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [hoveredSector, setHoveredSector] = useState<string | null>(null);
  const [highlightedCard, setHighlightedCard] = useState<string | null>(null);
  const gardenRef = useRef(null);
  const cardsRef = useRef<{
    [key: string]: HTMLDivElement | null;
  }>({});
  const handleNodeClick = (companyId: string) => {
    setHighlightedCard(companyId);
    const cardElement = cardsRef.current[companyId];
    if (cardElement) {
      cardElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };
  const founderStories = [{
    id: 'bopa',
    theme: 'GROW',
    company: 'BOPA',
    founderNames: ['Sarah Hinkley'],
    headshot: 'https://cdn.magicpatterns.com/uploads/j5YeKjhxpiTiQqzWAJbVtt/Sarah_Headshot.jpg',
    headshots: null,
    story: "Sarah Hinkley watched farmers in Colorado choose between soil health and profitability for years. She decided that was a false choice. BOPA's autonomous nano-tractors do the precision work that regenerative agriculture demands, at a scale and cost that makes it real for small and mid-size farms. Her ANTs are now operating across five states, restoring soil acre by acre. Colorado named her Emerging Tech Company of the Year. She's just getting started.",
    chips: ['Operating across 5 states', 'CO Emerging Tech Company of the Year'],
    website: 'https://barnowlag.com/',
    linkedin: 'https://www.linkedin.com/company/bopa-precision-ag/'
  }, {
    id: 'mothership',
    theme: 'USE',
    company: 'Mothership Materials',
    founderNames: ['Jo Marini'],
    headshot: 'https://cdn.magicpatterns.com/uploads/gRrYe5nDZcZx5f2NZAUVvp/Jo_Headshot.jpg',
    headshots: null,
    story: "Jo Marini is a two-time exit CEO who looked at the global biomanufacturing industry and saw a gap the size of a supply chain: companies generating tons of agricultural waste with nowhere to put it, and manufacturers needing raw inputs they couldn't afford to source. She built a containerized system that converts one into the other, the way microorganisms have always done, without heat or chemicals. AB InBev signed a pilot-to-scale deal. She has over $100 million in inbound pipeline. The world is starting to pay attention.",
    chips: ['AB InBev pilot-to-scale', '$100M+ inbound pipeline'],
    website: 'https://www.mothershipmaterials.com/',
    linkedin: 'https://www.linkedin.com/company/mothershipmaterials/'
  }, {
    id: 'reefcycle',
    theme: 'BUILD',
    company: 'ReefCycle',
    founderNames: ['Mary Lempres', 'Shannon Parker'],
    headshots: ['https://cdn.magicpatterns.com/uploads/4hhMPidW6aSsMkoc11grzo/Mary_Headshot.jpg', 'https://cdn.magicpatterns.com/uploads/dDkjGRK46uLePih7U1qyMZ/Shannon_Headshot.jpg'],
    story: "Conventional cement requires heating materials to over 2,500 degrees Fahrenheit. It is one of the most carbon-intensive industrial processes on Earth, and it has not fundamentally changed in a century. Mary Lempres asked a different question: how does a coral reef do it? The answer was room-temperature biomineralization. ReefCycle's cement cures 93% faster than conventional material, costs 75% less to produce, and is designed for the coastal infrastructure decisions being made right now — the ones that will determine whether communities remain livable for the next 50 years.",
    chips: ['93% faster cure', '75% lower production cost'],
    website: 'https://reefcycle.earth/',
    linkedin: 'https://www.linkedin.com/company/reefcycle/posts/?feedView=all'
  }, {
    id: 'tierrasphere',
    theme: 'GROW',
    company: 'TierraSphere',
    founderNames: ['Marcela Flores'],
    headshot: 'https://cdn.magicpatterns.com/uploads/9L48deLNJpBFgHmaVDUwzx/Marcela_Headshot.jpg',
    headshots: null,
    story: "Ninety percent of the world's topsoil is at risk by 2050. Marcela Flores found the answer growing in degraded land across Oaxaca. The Ramón tree restores soil through biomineralization and sequesters carbon in stable inorganic form for thousands of years. Its seeds produce a high-protein flour already ordered by Grupo Bimbo, one of the world's largest food companies. TierraSphere is rebuilding the ground beneath global food supply chains, one tree at a time. Nestlé, Pepsi, and Unilever are all investing in regenerative agriculture for exactly the reason Marcela already built for.",
    chips: ['Flour ordered by Grupo Bimbo', 'Carbon stable for millennia'],
    website: 'https://www.tierrasphere.com/',
    linkedin: 'https://www.linkedin.com/company/tierrasphere/'
  }];
  return <div className="min-h-screen bg-deepCanopy" data-id="element-1520">
      {/* Hero */}
      <section aria-label="Vitality portfolio of nature-inspired startups" className="relative min-h-[70vh] flex items-center justify-center overflow-hidden" data-id="element-1521">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D2B24] via-[#1F3A33] to-[#0D2B24]" data-id="element-1522" />

        {/* Subtle topographic lines */}
        <svg className="absolute inset-0 w-full h-full opacity-5" data-id="element-1523">
          {[...Array(8)].map((_, i) => <ellipse key={i} cx="50%" cy="50%" rx={`${20 + i * 10}%`} ry={`${15 + i * 8}%`} fill="none" stroke="#2BC7B7" strokeWidth="1" data-id="element-1524" />)}
        </svg>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1525">
          <motion.h1 className="text-6xl md:text-8xl font-bold text-white mb-8" initial={{
          opacity: 0
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.8
        }} data-id="element-1526">
            <MyceliumText as="span" delay={0.2} staggerChildren={0.05} data-id="element-1527">
              The Vitality Portfolio
            </MyceliumText>
          </motion.h1>

          <RevealWrapper delay={0.4} data-id="element-1528">
            <p className="text-2xl text-vitalityTeal mb-6 font-light" data-id="element-1529">
              Where Nature-Inspired Innovation Takes Root
            </p>
          </RevealWrapper>

          <RevealWrapper delay={0.5} data-id="element-1530">
            <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed" data-id="element-1531">
              Our portfolio grows from a single thesis: nature's 3.8 billion
              years of R&D holds the solutions to humanity's greatest
              challenges. These founders represent a seed of transformation,
              cultivated with intention and connected through shared purpose.
            </p>
          </RevealWrapper>
        </div>
      </section>

      {/* The Radial Garden Visualization */}
      <section ref={gardenRef} className="relative py-12 sm:py-20 bg-gradient-to-b from-[#0D2B24] to-[#1F3A33]" data-id="element-1532">
        <div className="max-w-7xl mx-auto px-4" data-id="element-1533">
          {/* Mobile hint */}
          <p className="text-center text-gray-500 text-sm mb-4 md:hidden" data-id="element-1534">
            Pinch to zoom • Tap companies to learn more
          </p>
          <div className="relative w-full max-w-5xl mx-auto overflow-x-auto md:overflow-visible" style={{
          aspectRatio: '1',
          minWidth: '320px'
        }} data-id="element-1535">
            <svg viewBox="0 0 800 800" className="w-full h-full" style={{
            filter: 'drop-shadow(0 0 40px rgba(43, 199, 183, 0.1))'
          }} data-id="element-1536">
              <defs data-id="element-1537">
                {/* Gradients for sectors */}
                <radialGradient id="growGradient" cx="50%" cy="50%" data-id="element-1538">
                  <stop offset="0%" stopColor="#2BC7B7" stopOpacity="0.2" data-id="element-1539" />
                  <stop offset="100%" stopColor="#2BC7B7" stopOpacity="0" data-id="element-1540" />
                </radialGradient>
                <radialGradient id="buildGradient" cx="50%" cy="50%" data-id="element-1541">
                  <stop offset="0%" stopColor="#4DE7D4" stopOpacity="0.2" data-id="element-1542" />
                  <stop offset="100%" stopColor="#4DE7D4" stopOpacity="0" data-id="element-1543" />
                </radialGradient>
                <radialGradient id="useGradient" cx="50%" cy="50%" data-id="element-1544">
                  <stop offset="0%" stopColor="#D4C5A9" stopOpacity="0.2" data-id="element-1545" />
                  <stop offset="100%" stopColor="#D4C5A9" stopOpacity="0" data-id="element-1546" />
                </radialGradient>

                {/* Glow filter */}
                <filter id="glow" data-id="element-1547">
                  <feGaussianBlur stdDeviation="4" result="coloredBlur" data-id="element-1548" />
                  <feMerge data-id="element-1549">
                    <feMergeNode in="coloredBlur" data-id="element-1550" />
                    <feMergeNode in="SourceGraphic" data-id="element-1551" />
                  </feMerge>
                </filter>
              </defs>

              {/* Garden bed background circle */}
              <motion.circle cx="400" cy="400" r="350" fill="#0D2B24" stroke="#2BC7B7" strokeWidth="2" strokeOpacity="0.2" initial={{
              scale: 0,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              duration: 1,
              ease: organicEase
            }} data-id="element-1552" />

              {/* Growth rings */}
              {[280, 220, 160, 100].map((r, i) => <motion.circle key={r} cx="400" cy="400" r={r} fill="none" stroke="#2BC7B7" strokeWidth="1" strokeOpacity="0.1" strokeDasharray="4 4" initial={{
              scale: 0,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              delay: 0.2 + i * 0.1,
              duration: 0.8
            }} data-id="element-1553" />)}

              {/* Three Sectors with organic edges */}
              {Object.entries(portfolioData).map(([key, theme], index) => {
              const startAngle = index * 120 - 90;
              const endAngle = startAngle + 120;
              const isHovered = hoveredSector === key;
              // Create organic sector path
              const createSectorPath = () => {
                const points: string[] = ['M 400 400'];
                const radius = 350;
                const steps = 30;
                for (let i = 0; i <= steps; i++) {
                  const angle = startAngle + 120 * i / steps;
                  const rad = angle * Math.PI / 180;
                  const wobble = Math.sin(i * 0.5) * 8;
                  const r = radius + wobble;
                  const x = 400 + r * Math.cos(rad);
                  const y = 400 + r * Math.sin(rad);
                  points.push(`${i === 0 ? 'L' : 'L'} ${x} ${y}`);
                }
                points.push('Z');
                return points.join(' ');
              };
              return <g key={key} data-id="element-1554">
                    <motion.path d={createSectorPath()} fill={`url(#${key}Gradient)`} stroke={theme.color} strokeWidth="2" strokeOpacity={isHovered ? 0.6 : 0.2} initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 0.5 + index * 0.1,
                  duration: 1
                }} onMouseEnter={() => setHoveredSector(key)} onMouseLeave={() => setHoveredSector(null)} style={{
                  cursor: 'pointer'
                }} data-id="element-1555" />

                    {/* Sector label */}
                    <motion.g initial={{
                  opacity: 0,
                  scale: 0
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  delay: 0.8 + index * 0.1,
                  duration: 0.6
                }} data-id="element-1556">
                      <text x={400 + 280 * Math.cos((startAngle + 60) * Math.PI / 180)} y={400 + 280 * Math.sin((startAngle + 60) * Math.PI / 180)} textAnchor="middle" fill={theme.color} fontSize="24" fontWeight="bold" data-id="element-1557">
                        {theme.title}
                      </text>
                      <text x={400 + 280 * Math.cos((startAngle + 60) * Math.PI / 180)} y={400 + 280 * Math.sin((startAngle + 60) * Math.PI / 180) + 20} textAnchor="middle" fill="#F3F5F1" fontSize="12" opacity="0.7" data-id="element-1558">
                        {theme.subtitle}
                      </text>
                      <text x={400 + 280 * Math.cos((startAngle + 60) * Math.PI / 180)} y={400 + 280 * Math.sin((startAngle + 60) * Math.PI / 180) + 36} textAnchor="middle" fill={theme.color} fontSize="11" fontWeight="600" data-id="element-1559">
                        {theme.allocation}
                      </text>
                    </motion.g>
                  </g>;
            })}

              {/* Root connections */}
              {Object.entries(portfolioData).map(([themeKey, theme]) => theme.companies.map((company, companyIndex) => {
              const angle = company.angle * Math.PI / 180;
              const x = 400 + company.distance * Math.cos(angle);
              const y = 400 + company.distance * Math.sin(angle);
              const isHovered = hoveredNode === company.id;
              // Create organic root path
              const controlX1 = 400 + company.distance * 0.6 * Math.cos(angle) + Math.random() * 20 - 10;
              const controlY1 = 400 + company.distance * 0.6 * Math.sin(angle) + Math.random() * 20 - 10;
              const controlX2 = 400 + company.distance * 0.3 * Math.cos(angle) + Math.random() * 20 - 10;
              const controlY2 = 400 + company.distance * 0.3 * Math.sin(angle) + Math.random() * 20 - 10;
              return <motion.g key={company.id} data-id="element-1560">
                      {/* Root path */}
                      <motion.path d={`M ${x} ${y} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, 400 400`} stroke={theme.color} strokeWidth={isHovered ? 3 : 2} strokeOpacity={isHovered ? 0.6 : 0.3} fill="none" strokeLinecap="round" initial={{
                  pathLength: 0,
                  opacity: 0
                }} animate={{
                  pathLength: 1,
                  opacity: 1
                }} transition={{
                  delay: 1.2 + companyIndex * 0.15,
                  duration: 1.5,
                  ease: organicEase
                }} data-id="element-1561" />

                      {/* Animated pulse along root */}
                      <motion.circle r="3" fill={theme.color} filter="url(#glow)" initial={{
                  opacity: 0
                }} animate={{
                  opacity: [0, 0.8, 0]
                }} transition={{
                  delay: 2 + companyIndex * 0.5,
                  duration: 3,
                  repeat: Infinity,
                  repeatDelay: 5
                }} data-id="element-1562">
                        <animateMotion dur="3s" repeatCount="indefinite" begin={`${2 + companyIndex * 0.5}s`} data-id="element-1563">
                          <mpath href={`#root-${company.id}`} xlinkHref={`#root-${company.id}`} data-id="element-1564" />
                        </animateMotion>
                      </motion.circle>

                      {/* Hidden path for animation */}
                      <path id={`root-${company.id}`} d={`M ${x} ${y} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, 400 400`} fill="none" opacity="0" data-id="element-1565" />
                    </motion.g>;
            }))}

              {/* Center Core - The Thesis */}
              <motion.g initial={{
              scale: 0,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              delay: 0.3,
              duration: 1,
              ease: organicEase
            }} data-id="element-1566">
                {/* Pulsing outer ring */}
                <motion.circle cx="400" cy="400" r="70" fill="none" stroke="#4DE7D4" strokeWidth="2" strokeOpacity="0.4" animate={{
                r: [70, 80, 70],
                strokeOpacity: [0.4, 0.1, 0.4]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-1567" />

                {/* Core orb */}
                <motion.circle cx="400" cy="400" r="60" fill="url(#coreGradient)" filter="url(#glow)" animate={{
                opacity: [0.8, 1, 0.8]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-1568" />

                <defs data-id="element-1569">
                  <radialGradient id="coreGradient" data-id="element-1570">
                    <stop offset="0%" stopColor="#4DE7D4" data-id="element-1571" />
                    <stop offset="100%" stopColor="#2BC7B7" data-id="element-1572" />
                  </radialGradient>
                </defs>

                {/* Core text */}
                <text x="400" y="400" textAnchor="middle" fill="#0D2B24" fontSize="12" fontWeight="700" data-id="element-1573">
                  VITALITY
                </text>
                <text x="400" y="415" textAnchor="middle" fill="#0D2B24" fontSize="11" fontWeight="600" data-id="element-1574">
                  THESIS
                </text>
              </motion.g>

              {/* Company Plant Nodes */}
              {Object.entries(portfolioData).map(([themeKey, theme]) => theme.companies.map((company, companyIndex) => {
              const angle = company.angle * Math.PI / 180;
              const x = 400 + company.distance * Math.cos(angle);
              const y = 400 + company.distance * Math.sin(angle);
              const isHovered = hoveredNode === company.id;
              const isSeed = company.stage === 'Seed';
              return <motion.g key={company.id} initial={{
                scale: 0,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                delay: 1.5 + companyIndex * 0.2,
                duration: 0.8,
                ease: organicEase
              }} whileHover={{
                scale: 1.15
              }} onMouseEnter={() => setHoveredNode(company.id)} onMouseLeave={() => setHoveredNode(null)} onClick={() => handleNodeClick(company.id)} style={{
                cursor: 'pointer'
              }} data-id="element-1575">
                      {/* Bloom glow ring */}
                      <motion.circle cx={x} cy={y} r={isSeed ? 45 : 35} fill="none" stroke={theme.color} strokeWidth={isHovered ? 4 : 2} strokeOpacity={isHovered ? 0.8 : 0.4} animate={{
                  r: isSeed ? [45, 48, 45] : [35, 38, 35]
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-1576" />

                      {/* Bloom center (founder headshot instead of logo) */}
                      <circle cx={x} cy={y} r={isSeed ? 38 : 30} fill="#FFFFFF" data-id="element-1577" />

                      {/* Founder headshot */}
                      <foreignObject x={x - (isSeed ? 38 : 30)} y={y - (isSeed ? 38 : 30)} width={(isSeed ? 38 : 30) * 2} height={(isSeed ? 38 : 30) * 2} data-id="element-1578">
                        <div style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    display: 'flex'
                  }} data-id="element-1579">
                          {company.headshots ? <>
                              <img src={company.headshots[0]} alt="Founder 1" style={{
                        width: '50%',
                        height: '100%',
                        objectFit: 'cover'
                      }} data-id="element-1580" />
                              <img src={company.headshots[1]} alt="Founder 2" style={{
                        width: '50%',
                        height: '100%',
                        objectFit: 'cover'
                      }} data-id="element-1581" />
                            </> : <img src={company.headshot} alt={`${company.founder} headshot`} style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }} data-id="element-1582" />}
                        </div>
                      </foreignObject>

                      {/* Stage indicator (petals/leaves) */}
                      {isSeed ?
                // Fuller bloom for Seed stage
                <>
                          {[0, 72, 144, 216, 288].map((petalAngle, i) => {
                    const rad = (petalAngle + company.angle) * Math.PI / 180;
                    const px = x + 42 * Math.cos(rad);
                    const py = y + 42 * Math.sin(rad);
                    return <motion.circle key={i} cx={px} cy={py} r="6" fill={theme.color} opacity="0.6" initial={{
                      scale: 0
                    }} animate={{
                      scale: 1
                    }} transition={{
                      delay: 1.7 + companyIndex * 0.2 + i * 0.05
                    }} data-id="element-1583" />;
                  })}
                        </> :
                // Seedling leaves for Pre-Seed
                <>
                          <motion.ellipse cx={x - 25} cy={y - 10} rx="8" ry="12" fill={theme.color} opacity="0.5" initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} transition={{
                    delay: 1.7 + companyIndex * 0.2
                  }} data-id="element-1584" />
                          <motion.ellipse cx={x + 25} cy={y - 10} rx="8" ry="12" fill={theme.color} opacity="0.5" initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} transition={{
                    delay: 1.75 + companyIndex * 0.2
                  }} data-id="element-1585" />
                        </>}

                      {/* Company name label */}
                      <text x={x} y={y + (isSeed ? 60 : 50)} textAnchor="middle" fill="#F3F5F1" fontSize="13" fontWeight="600" data-id="element-1586">
                        {company.name}
                      </text>

                      {/* Founder name */}
                      <text x={x} y={y + (isSeed ? 75 : 65)} textAnchor="middle" fill={theme.color} fontSize="10" opacity="0.8" data-id="element-1587">
                        {company.founder || company.founders?.join(' & ')}
                      </text>

                      {/* Hover tooltip */}
                      {isHovered && <motion.g initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-1588">
                          <rect x={x - 80} y={y - 120} width="160" height="60" rx="8" fill="#0A1111" opacity="0.95" stroke={theme.color} strokeWidth="2" data-id="element-1589" />
                          <text x={x} y={y - 95} textAnchor="middle" fill={theme.color} fontSize="11" fontWeight="600" data-id="element-1590">
                            {company.opportunity} opportunity
                          </text>
                          <text x={x} y={y - 75} textAnchor="middle" fill="#F3F5F1" fontSize="9" opacity="0.9" data-id="element-1591">
                            {company.traction.slice(0, 40)}
                            {company.traction.length > 40 ? '...' : ''}
                          </text>
                        </motion.g>}
                    </motion.g>;
            }))}
            </svg>
          </div>
        </div>
      </section>

      {/* Founder Stories - Editorial */}
      <section aria-label="Portfolio founder stories" className="relative py-24 md:py-32 bg-[#0A1111] overflow-hidden" data-id="element-1592">
        {/* Subtle background texture */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.03]" data-id="element-1593">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-1594">
            <filter id="founder-noise" data-id="element-1595">
              <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="4" stitchTiles="stitch" data-id="element-1596" />
            </filter>
            <rect width="100%" height="100%" filter="url(#founder-noise)" data-id="element-1597" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1598">
          {/* Framing block */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="mb-20 md:mb-28 max-w-4xl mx-auto" data-id="element-1599">
            <div className="bg-[#0D2B24] border border-vitalityTeal/15 rounded-organic-lg p-8 md:p-12 relative" data-id="element-1600">
              <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-vitalityTeal/40 to-transparent" data-id="element-1601" />
              <div className="flex items-center gap-3 mb-6" data-id="element-1602">
                <div className="h-px w-10 bg-vitalityTeal/40" data-id="element-1603" />
                <span className="text-vitalityTeal text-xs font-semibold tracking-[0.2em] uppercase" data-id="element-1604">
                  Founder Stories
                </span>
              </div>
              <p className="text-myceliumWhite/90 text-lg md:text-xl leading-relaxed font-light" data-id="element-1605">
                These founders are applying 3.8 billion years of nature's R&D to
                the problems that matter most. They are generating revenue,
                landing pilots with global brands, and building in the physical
                world. They are also largely invisible to the capital system
                that could fund them. That is the gap Vitality exists to close.
              </p>
            </div>
          </motion.div>

          {/* Stories list */}
          <div className="relative space-y-24 md:space-y-32" data-id="element-1606">
            {founderStories.map((story, index) => {
            const isReversed = index % 2 === 1;
            const isHighlighted = highlightedCard === story.id;
            return <motion.div key={story.id} ref={el => cardsRef.current[story.id] = el} initial={{
              opacity: 0,
              y: 40
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-100px'
            }} transition={{
              duration: 0.8
            }} className={`relative group flex flex-col ${isReversed ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-10 md:gap-16 transition-all duration-500 ${isHighlighted ? 'ring-2 ring-vitalityTeal ring-offset-8 ring-offset-[#0A1111] rounded-organic-lg p-4 md:p-6' : ''}`} data-id="element-1607">
                  {/* Subtle hover edge glow */}
                  <div className="absolute -inset-4 rounded-organic-lg bg-gradient-to-r from-vitalityTeal/0 via-bioLuminescentTeal/0 to-vitalityTeal/0 group-hover:from-vitalityTeal/5 group-hover:via-bioLuminescentTeal/10 group-hover:to-vitalityTeal/5 blur-2xl transition-all duration-700 pointer-events-none" data-id="element-1608" />

                  {/* Portrait */}
                  <div className="w-full md:w-2/5 relative z-10" data-id="element-1609">
                    {story.headshots ? <div className="grid grid-cols-2 gap-3" data-id="element-1610">
                        {story.headshots.map((src, idx) => <div key={idx} className="aspect-square overflow-hidden rounded-organic-lg border border-vitalityTeal/20 bg-[#0D2B24] group/img" data-id="element-1611">
                            <img src={src} alt={`${story.founderNames[idx]} headshot — ${story.company} co-founder`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover/img:scale-105" data-id="element-1612" />
                          </div>)}
                      </div> : <div className="aspect-[4/5] overflow-hidden rounded-organic-lg border border-vitalityTeal/20 bg-[#0D2B24] group/img" data-id="element-1613">
                        <img src={story.headshot} alt={`${story.founderNames[0]} headshot — ${story.company} founder`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover/img:scale-105" data-id="element-1614" />
                      </div>}
                  </div>

                  {/* Content */}
                  <div className="w-full md:w-3/5 relative z-10" data-id="element-1615">
                    {/* Theme eyebrow */}
                    <div className="flex items-center gap-3 mb-5" data-id="element-1616">
                      <span className="text-earthClay text-xs font-semibold tracking-[0.25em] uppercase" data-id="element-1617">
                        {story.theme}
                      </span>
                      <div className="h-px flex-1 max-w-[60px] bg-earthClay/30" data-id="element-1618" />
                    </div>

                    {/* Founder name + company */}
                    <h3 className="font-serif text-3xl md:text-5xl text-myceliumWhite mb-2 leading-[1.1] tracking-tight" data-id="element-1619">
                      {story.founderNames.join(' + ')}
                    </h3>
                    <p className="text-vitalityTeal text-lg md:text-xl font-medium mb-6 tracking-wide" data-id="element-1620">
                      {story.company}
                    </p>

                    {/* Story */}
                    <p className="text-myceliumWhite/75 text-base md:text-lg leading-relaxed mb-7 font-light" data-id="element-1621">
                      {story.story}
                    </p>

                    {/* Proof chips */}
                    <div className="flex flex-wrap gap-2 mb-7" data-id="element-1622">
                      {story.chips.map((chip, idx) => <span key={idx} className="inline-flex items-center px-3.5 py-1.5 rounded-full text-xs font-medium bg-vitalityTeal/10 text-vitalityTeal border border-vitalityTeal/25 tracking-wide" data-id="element-1623">
                          {chip}
                        </span>)}
                    </div>

                    {/* Links */}
                    <div className="flex items-center gap-4" data-id="element-1624">
                      {story.website && <a href={story.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-vitalityTeal hover:text-bioLuminescentTeal transition-colors text-sm font-medium group/link" data-id="element-1625">
                          <ExternalLink className="w-3.5 h-3.5" data-id="element-1626" />
                          <span className="relative" data-id="element-1627">
                            Visit {story.company}
                            <span className="absolute left-0 -bottom-0.5 w-0 h-px bg-bioLuminescentTeal group-hover/link:w-full transition-all duration-300" data-id="element-1628" />
                          </span>
                        </a>}
                      {story.linkedin && <a href={story.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${story.company} on LinkedIn`} className="text-myceliumWhite/40 hover:text-vitalityTeal transition-colors" data-id="element-1629">
                          <Linkedin className="w-4 h-4" data-id="element-1630" />
                        </a>}
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Detailed Portfolio Cards - Botanical Specimen Style */}
      <section aria-label="Portfolio company details and growth metrics" className="relative py-32 bg-gradient-to-b from-[#1F3A33] to-[#0D2B24]" data-id="element-1631">
        <div className="max-w-7xl mx-auto px-4" data-id="element-1632">
          <RevealWrapper data-id="element-1633">
            <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-4" data-id="element-1634">
              <MyceliumText as="span" staggerChildren={0.04} data-id="element-1635">
                Portfolio Deep Roots
              </MyceliumText>
            </h2>
          </RevealWrapper>

          <RevealWrapper delay={0.2} data-id="element-1636">
            <p className="text-vitalityTeal text-center mb-16" data-id="element-1637">
              Pre-Fund 1 Investments
            </p>
          </RevealWrapper>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-1638">
            {Object.entries(portfolioData).map(([themeKey, theme]) => theme.companies.map((company, index) => <motion.div key={company.id} ref={el => cardsRef.current[company.id] = el} className={`relative p-8 rounded-2xl bg-[#F3F5F1] border-4 transition-all duration-300 ${highlightedCard === company.id ? 'ring-4 ring-vitalityTeal shadow-2xl' : 'shadow-lg'}`} style={{
            borderColor: theme.color,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="100" height="100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M50 10 Q 30 30 50 50 T 50 90" stroke="%23D4C5A9" stroke-width="0.5" fill="none" opacity="0.1"/%3E%3C/svg%3E")'
          }} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.6
          }} whileHover={{
            y: -8,
            boxShadow: `0 20px 40px ${theme.color}40`
          }} data-id="element-1639">
                  {/* Theme bar */}
                  <div className="absolute top-0 left-0 right-0 h-2 rounded-t-2xl" style={{
              backgroundColor: theme.color
            }} data-id="element-1640" />

                  {/* Header */}
                  <div className="flex items-center gap-2 mb-4" data-id="element-1641">
                    <span className="px-3 py-1 rounded-full text-xs font-bold uppercase" style={{
                backgroundColor: `${theme.color}20`,
                color: theme.color
              }} data-id="element-1642">
                      {theme.title} • {theme.subtitle}
                    </span>
                  </div>

                  {/* Company Info */}
                  <div className="flex items-start gap-6 mb-6" data-id="element-1643">
                    {company.headshots ?
              // Multiple founders - show both headshots
              <div className="flex -space-x-4" data-id="element-1644">
                        {company.headshots.map((headshot: string, idx: number) => <div key={idx} className="w-20 h-20 rounded-full border-4 flex-shrink-0 overflow-hidden" style={{
                  backgroundColor: `${theme.color}15`,
                  borderColor: theme.color
                }} data-id="element-1645">
                              <img src={headshot} alt={`${company.founders?.[idx]} headshot`} className="w-full h-full object-cover" data-id="element-1646" />
                            </div>)}
                      </div> :
              // Single founder
              <div className="w-20 h-20 rounded-full border-4 flex-shrink-0 overflow-hidden" style={{
                backgroundColor: `${theme.color}15`,
                borderColor: theme.color
              }} data-id="element-1647">
                        <img src={company.headshot} alt={`${company.founder} headshot`} className="w-full h-full object-cover" data-id="element-1648" />
                      </div>}
                    <div className="flex-1" data-id="element-1649">
                      <div className="mb-3" data-id="element-1650">
                        <img src={company.logo} alt={`${company.name} logo`} className="h-8 object-contain" style={{
                    maxWidth: '200px'
                  }} data-id="element-1651" />
                      </div>
                      <p className="text-gray-600 text-sm italic mb-2" data-id="element-1652">
                        {company.tagline}
                      </p>
                      <p className="font-semibold" style={{
                  color: theme.color
                }} data-id="element-1653">
                        {company.founder || company.founders?.join(' & ')}
                      </p>
                      <p className="text-gray-500 text-xs" data-id="element-1654">
                        {company.title || company.titles?.join(' | ')}
                      </p>
                    </div>
                  </div>

                  {/* Growth Metrics - Styled like plant care instructions */}
                  <div className="bg-white/60 rounded-xl p-6 mb-4" data-id="element-1655">
                    <p className="text-xs font-bold text-gray-500 uppercase mb-4 tracking-wider" data-id="element-1656">
                      Growth Metrics
                    </p>
                    <div className="space-y-3" data-id="element-1657">
                      <div className="flex items-center justify-between" data-id="element-1658">
                        <span className="text-gray-600 text-sm flex items-center gap-2" data-id="element-1659">
                          <span data-id="element-1660">🌱</span> STAGE
                        </span>
                        <span className="font-semibold" style={{
                    color: theme.color
                  }} data-id="element-1661">
                          {company.stage}
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-1662">
                        <span className="text-gray-600 text-sm flex items-center gap-2" data-id="element-1663">
                          <span data-id="element-1664">🌍</span> OPPORTUNITY
                        </span>
                        <span className="font-semibold" style={{
                    color: theme.color
                  }} data-id="element-1665">
                          {company.opportunity}
                        </span>
                      </div>
                      <div className="flex items-start justify-between" data-id="element-1666">
                        <span className="text-gray-600 text-sm flex items-center gap-2" data-id="element-1667">
                          <span data-id="element-1668">📈</span> TRACTION
                        </span>
                        <span className="font-semibold text-right max-w-[60%] text-sm" style={{
                    color: theme.color
                  }} data-id="element-1669">
                          {company.traction}
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-1670">
                        <span className="text-gray-600 text-sm flex items-center gap-2" data-id="element-1671">
                          <span data-id="element-1672">🤝</span> CO-INVESTORS
                        </span>
                        <span className="font-semibold text-right max-w-[60%] text-sm" style={{
                    color: theme.color
                  }} data-id="element-1673">
                          {company.coInvestors}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Pressed leaf watermark */}
                  <div className="absolute bottom-4 right-4 opacity-5 pointer-events-none" style={{
              fontSize: '80px'
            }} data-id="element-1674">
                    🍃
                  </div>

                  {/* Links */}
                  <div className="flex items-center gap-3 mt-4" data-id="element-1675">
                    {company.website && <a href={company.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px]" style={{
                backgroundColor: `${theme.color}15`,
                color: theme.color
              }} onClick={e => e.stopPropagation()} data-id="element-1676">
                        <ExternalLink className="w-4 h-4" data-id="element-1677" />
                        Visit Website
                      </a>}
                    {company.linkedin && <a href={company.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-11 h-11 rounded-lg transition-colors" style={{
                backgroundColor: `${theme.color}15`,
                color: theme.color
              }} onClick={e => e.stopPropagation()} data-id="element-1678">
                        <Linkedin className="w-4 h-4" data-id="element-1679" />
                      </a>}
                  </div>
                </motion.div>))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="relative py-8 bg-[#0D2B24]" data-id="element-1680">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1681">
          <p className="text-sm text-gray-500" data-id="element-1682">
            Successful pre-fund investments are anticipated to be transferred to
            Fund 1 at cost, subject to their continued success and alignment
            with Fund 1's investment thesis
          </p>
        </div>
      </section>

      {/* Contact CTA - Plant a Seed */}
      <section className="relative py-32 bg-gradient-to-b from-[#0D2B24] to-[#0A1111]" data-id="element-1683">
        {/* Subtle root tendrils at edges */}
        <svg className="absolute inset-0 w-full h-full opacity-10 pointer-events-none" data-id="element-1684">
          <path d="M 0 100 Q 100 80, 200 100 T 400 100" stroke="#2BC7B7" strokeWidth="2" fill="none" data-id="element-1685" />
          <path d="M 800 100 Q 700 120, 600 100 T 400 100" stroke="#2BC7B7" strokeWidth="2" fill="none" data-id="element-1686" />
        </svg>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1687">
          <RevealWrapper data-id="element-1688">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8" data-id="element-1689">
              <MyceliumText as="span" staggerChildren={0.04} data-id="element-1690">
                Plant a Seed With Us
              </MyceliumText>
            </h2>
          </RevealWrapper>

          <RevealWrapper delay={0.2} data-id="element-1691">
            <p className="text-xl text-gray-300 mb-12" data-id="element-1692">
              Whether you're a founder with a nature-inspired solution or an
              investor aligned with our thesis, we'd love to connect.
            </p>
          </RevealWrapper>

          <RevealWrapper delay={0.4} data-id="element-1693">
            <div className="flex flex-col items-center gap-6" data-id="element-1694">
              <motion.div className="w-24 h-24 rounded-full bg-vitalityTeal/20 border-4 border-vitalityTeal flex items-center justify-center overflow-hidden" animate={{
              boxShadow: ['0 0 20px rgba(43, 199, 183, 0.3)', '0 0 40px rgba(43, 199, 183, 0.6)', '0 0 20px rgba(43, 199, 183, 0.3)']
            }} transition={{
              duration: 3,
              repeat: Infinity
            }} data-id="element-1695">
                <img src="https://cdn.magicpatterns.com/uploads/nPrbWRd99BLA7hL4gLSpQo/7_Natalya.jpg" alt="Natalya Wallin" className="w-full h-full object-cover" data-id="element-1696" />
              </motion.div>
              <div data-id="element-1697">
                <p className="text-2xl font-bold text-white" data-id="element-1698">Natalya Wallin</p>
                <p className="text-vitalityTeal" data-id="element-1699">Founding Partner & GP</p>
                <a href="mailto:natalya@vitalityvf.com" className="text-gray-400 hover:text-vitalityTeal transition-colors" data-id="element-1700">
                  natalya@vitalityvf.com
                </a>
                <div className="flex items-center justify-center gap-4 mt-2" data-id="element-1701">
                  <a href="https://www.linkedin.com/company/vitality-vf/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-vitalityTeal transition-colors" data-id="element-1702">
                    <Linkedin className="w-5 h-5" data-id="element-1703" />
                  </a>
                </div>
              </div>
              <motion.div whileHover={{
              scale: 1.05,
              boxShadow: '0 0 30px rgba(43, 199, 183, 0.5)'
            }} whileTap={{
              scale: 0.95
            }} data-id="element-1704">
                <Button variant="primary" className="rounded-full" data-id="element-1705">
                  Get in Touch
                  <ArrowRight className="w-4 h-4 ml-2" data-id="element-1706" />
                </Button>
              </motion.div>
            </div>
          </RevealWrapper>
        </div>
      </section>
    </div>;
}